import AppActions from "../actions/AppActions";
import SiteActions from "../actions/SiteActions";
import UIActions from "../actions/UIActions";
import UserActions from "../actions/UserActions";
import config from "../config/app";
import AppConstants from "../constants/AppConstants";
import {
  applyAccountInfo,
  applyLoginData,
  getAccountData,
  getUserId,
  isAdmin,
  isContributing,
  mergeAccountData,
  resetAccount,
} from "../hooks/query/useAccount";
import { getSiteId, getSiteUserID } from "../hooks/query/useSite";
import APIError from "../service/APIError";
import * as auth from "../service/auth";
import LibraryService from "../service/LibraryService";
import UserService from "../service/UserService";
import ErrorUtils from "../utils/ErrorUtils";
import { prepareCreationData } from "../utils/LibraryUtils";
import { queryClient } from "../utils/QueryUtils";
import { trackEvent } from "../utils/TagUtils";
// Register action-to-cache listeners before initialization actions fire.
import "../hooks/query/useAssetFolders";
import "../hooks/query/useUserAssets";
import { addSiteToCache, userSitesQuery } from "../hooks/query/useSites";
import {
  bootIntercom,
  identifyPostHog,
  loadSatismeter,
  resetPostHog,
  shutdownIntercom,
  updateIntercomStatus,
} from "../adaptors/AnalyticsAdaptors";

/** @typedef {import("@/types/data/LoginResponse.mjs").LoginResponse} LoginResponse */

const {
  USER_STATUS_TRIAL,
  USER_STATUS_ACTIVE,
  USER_STATUS_INACTIVE,
  USER_STATUS_TRIAL_ENDED,
  MAIN_VIEW_SUBSCRIBE,
  MAIN_VIEW_USER_SITE_CHOOSER,
  MAIN_VIEW_LIBRARY_SITE_CHOOSER,
  INIT_DESIGN,
} = AppConstants;

/** Restores a persisted session or shows login. */
export const handleInitialize = () => {
  const token = auth.getStoredToken();

  if (token) {
    auth.setSessionToken(token);
    mergeAccountData({ token });
    UserActions.refreshToken();
  } else {
    UIActions.showLogin();
  }
};

/**
 * Applies a completed login and starts account loading.
 * @param {LoginResponse} data
 */
export const handleLoginComplete = (data) => {
  const { token, user } = data;

  // biome-ignore lint/suspicious/noDocumentCookie: legacy marketing cookie, ported verbatim
  document.cookie = "visited_design=true; path=/; domain=.showit.com; max-age=31536000";
  trackEvent("design_app_view", { version: "design.showit.com" });

  const clientConfig = data.config;

  // assign config properties to global config
  Object.assign(config, clientConfig);

  if (Object.hasOwn(clientConfig ?? {}, "demoMode")) {
    config.demoMode = true;
  }

  config.sitesBucket = config.designsBucket.replace("designs", "sites");
  config.designsBackupBucket = config.designsBucket.replace("designs", "design-backups");
  config.assetURL = (config.assetURL ?? "").replace("http:", "");

  auth.setLocalStorageToken(token);
  auth.registerVisibilityHandler();

  auth.setSessionToken(token);
  auth.setSessionUserId(user.user_id);

  applyLoginData(data);

  bootIntercom(user);
  identifyPostHog(user);

  if (isContributing()) {
    UserActions.trackEvent({
      event: "contributor-login",
      meta: {
        time: new Date().toUTCString(),
        user: `${user.actingContributor.first_name} ${user.actingContributor.last_name} - ${user.actingContributor.id}`,
      },
    });
  }

  UserActions.loadAccountInfo(getUserId());
};

/**
 * Loads and applies account information.
 * @param {number} userId
 */
export const handleLoadAccountInfo = async (userId) => {
  try {
    const data = await UserService.loadAccount(userId);
    applyAccountInfo(data.account);
    completeLoadAccountInfo();
  } catch (err) {
    if (APIError.handle(err)) {
      return;
    }
    AppActions.showAppError(ErrorUtils.getFormattedError(err));
  }
};

/** Routes the user after account loading. */
export const completeLoadAccountInfo = () => {
  const status = getAccountData()?.status;

  updateIntercomStatus(status);

  switch (status) {
    case USER_STATUS_TRIAL:
    case USER_STATUS_ACTIVE:
      loadAssetsAndSites();
      loadSatismeter();
      break;
    case USER_STATUS_INACTIVE:
    case USER_STATUS_TRIAL_ENDED:
      if (isAdmin()) {
        loadAssetsAndSites();
      } else {
        UIActions.showAppView({ view: MAIN_VIEW_SUBSCRIBE });
      }
      break;
  }
};

/**
 * Loads assets, folders, and the initial site or site chooser.
 */
export const loadAssetsAndSites = () => {
  const lastLoadedSiteId = getSiteId();

  if (config.demoMode && config.demoModeSiteDesignId) {
    SiteActions.loadSite({ site_design_id: config.demoModeSiteDesignId });
  }

  UserActions.loadAssets();
  UserActions.loadFolders(getUserId());

  const initView = config.initView ?? null;

  if (
    initView === null &&
    typeof lastLoadedSiteId === "number" &&
    (getSiteUserID() === getUserId() || isAdmin())
  ) {
    SiteActions.loadSite({ site_design_id: lastLoadedSiteId });
  } else {
    // Fetch the site list into the USER_SITES cache (so the chooser's `useSites` reads it without a second request -- staleTime: Infinity), then choose the boot view.
    queryClient
      .fetchQuery(userSitesQuery)
      .then((data) => decideInitialView(data?.sites ?? []))
      .catch(handleLoadSitesError);
  }
};

/**
 * Handles initial site-list loading errors.
 * @param {Error} err
 */
export const handleLoadSitesError = (err) => {
  if (APIError.handle(err)) {
    return;
  }
  const formatted = ErrorUtils.getFormattedError(err);
  formatted.userMessage = "Unable To Load Sites";
  AppActions.showAppError(formatted);
};

/**
 * Chooses the initial view from the loaded site list.
 * @param {Array<Object>} sites
 */
export const decideInitialView = (sites) => {
  const initView = config.initView ?? null;

  if (sites.length > 0) {
    if (initView === null && 1 === sites.length) {
      SiteActions.loadSite({ site_design_id: sites[0].site_design_id });
    } else if ("newest-design" === initView) {
      SiteActions.loadSite({
        site_design_id: Math.max(...sites.map((s) => s.site_design_id)),
      });
    } else {
      UIActions.showAppView({ view: MAIN_VIEW_USER_SITE_CHOOSER });
    }
  } else {
    // add and load initial design
    if (localStorage.getItem(INIT_DESIGN) && !isContributing()) {
      loadStarterDesign();
    } else {
      UIActions.showAppView({ view: MAIN_VIEW_LIBRARY_SITE_CHOOSER });
    }
  }
};

/**
 * Loads a starter design or falls back to the library chooser.
 */
export const loadStarterDesign = async () => {
  try {
    const libraryKey = Number(localStorage.getItem(INIT_DESIGN));
    const { sites = [] } = await LibraryService.loadSites();
    const site = sites.find((s) => s.lib_design_id === libraryKey);

    if (!site) throw new Error("Design unavailable");

    const { lib_design_id, design_id, pub_id, export_id, name } = site;
    const meta = { lib_design_id, design_id, pub_id, export_id, name };
    const creationInfo = prepareCreationData(site);

    const { site: newSite } = await UserService.createSite(creationInfo);
    UserActions.trackEvent({ event: "site-create", meta });

    // Boot already cached { sites: [] } with staleTime Infinity; without this
    // the chooser would show an empty list all session.
    addSiteToCache(newSite);

    SiteActions.loadSite(newSite);
    UIActions.showDesignView();
  } catch {
    UIActions.showAppView({ view: MAIN_VIEW_LIBRARY_SITE_CHOOSER });
  }

  localStorage.removeItem(INIT_DESIGN);
};

/**
 * Applies a failed login.
 * @param {Object} err
 */
export const handleLoginFail = (err) => {
  auth.clearLocalStorage();

  UIActions.showLogin();

  mergeAccountData({ loginError: err.error ? err.error : err });
};

/** Clears local session state on logout. */
export const handleLogout = () => {
  auth.clearAuth();
  resetAccount();
  resetPostHog();
  shutdownIntercom();
};

/** Reloads after logout completes. */
export const handleLogoutComplete = () => {
  location.reload();
};

/** Reloads after logout fails. */
export const handleLogoutFail = () => {
  location.reload();
};

/**
 * `UserActions.switchAccount.completed` seam.
 * @param {{token: string}} data
 */
export const handleSwitchAccountComplete = ({ token }) => {
  const url = new URL(window.location.href);
  url.searchParams.set("admin_token", token);
  window.location.href = url.href;
};

/**
 * `UserActions.switchAccount.failed` seam.
 * @param {Error} err
 */
export const handleSwitchAccountFail = (err) => {
  if (!APIError.handle(err)) {
    console.error(err);
  }
};

/**
 * `UserActions.refreshUserModal.completed` seam.
 * @param {{token: string, user: Object}} data
 */
export const handleRefreshUserModalComplete = (data) => {
  const { token, user } = data;
  auth.setLocalStorageToken(token);
  auth.setSessionToken(token);
  auth.setSessionUserId(user.user_id);
  user.token = token;
  mergeAccountData(user);
};

/**
 * `UserActions.openStore` seam.
 * @param {{location: string, newWindow?: boolean}} data
 */
export const handleOpenStore = (data) => {
  if (config.store) {
    const userId = getUserId();
    const storeLoc = data.location;
    const storeURL = `${config.store}/${storeLoc}?si-token=${getAccountData()?.token}&userId=${userId}`;

    if (Object.hasOwn(data, "newWindow")) {
      window.open(storeURL);
    } else {
      window.location = storeURL;
    }
  }
};

// ---- Action -> orchestration bindings.
UserActions.initialize.listen(handleInitialize);
UserActions.login.completed.listen(handleLoginComplete);
UserActions.login.failed.listen(handleLoginFail);
UserActions.logout.listen(handleLogout);
UserActions.logout.completed.listen(handleLogoutComplete);
UserActions.logout.failed.listen(handleLogoutFail);
UserActions.loadAccountInfo.listen(handleLoadAccountInfo);
UserActions.switchAccount.completed.listen(handleSwitchAccountComplete);
UserActions.switchAccount.failed.listen(handleSwitchAccountFail);
UserActions.refreshUserModal.completed.listen(handleRefreshUserModalComplete);
UserActions.openStore.listen(handleOpenStore);
